<template>
  <v-dialog v-model="s_dialogForLogin" max-width="500">
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon v-if="showPasswordReset" color="primary" class="mr-2">mdi-lock-reset</v-icon>
        <v-icon v-else-if="user" color="success" class="mr-2">mdi-account-check</v-icon>
        <v-icon v-else color="primary" class="mr-2">mdi-account</v-icon>
        <span v-if="user" class="oh3-text-lg">
          {{ showPasswordReset ? 'パスワード再設定' : `ようこそ、${displayNameToShow}さん！` }}
        </span>
        <span v-else class="oh3-text-lg">
          {{ showPasswordReset ? 'パスワード再設定' : (signUpDiv ? '新規登録' : 'ログイン') }}
        </span>
      </v-card-title>

      <v-card-text>
        <!-- ログイン済みの場合 -->
        <div v-if="user && !showPasswordReset" class="oh3-mt-3">
          <v-btn
              prepend-icon="mdi-logout"
              @click="logOut"
          >
            ログアウト
          </v-btn>

          <v-btn
              class="oh3-ml-3"
              @click="nicknameDivOpen = true"
          >
            ニックネーム変更
          </v-btn>

          <!-- パスワード再設定（ログイン済み用） -->
          <div class="mt-4 d-flex align-center" style="gap: var(--oh3-spacing-4);">
            <a href="#" class="forgot-password-link" @click.prevent="openPasswordResetForLoggedIn">
              パスワード再設定
            </a>
            <a href="#" class="delete-account-link" @click.prevent="showDeleteConfirm = true">
              アカウント削除
            </a>
          </div>

          <!-- アカウント削除確認 -->
          <v-alert
              v-if="showDeleteConfirm"
              type="error"
              variant="tonal"
              density="comfortable"
              class="mt-4"
              border="start"
          >
            <div class="oh3-font-bold oh3-mb-2">本当にアカウントを削除しますか？</div>
            <p class="oh3-mb-3">この操作は取り消せません。ログインできなくなります。</p>
            <div class="d-flex" style="gap: var(--oh3-spacing-2);">
              <v-btn
                  color="error"
                  size="small"
                  prepend-icon="mdi-delete-alert"
                  :loading="deleteLoading"
                  @click="deleteAccount"
              >
                削除する
              </v-btn>
              <v-btn
                  size="small"
                  variant="outlined"
                  @click="showDeleteConfirm = false"
              >
                キャンセル
              </v-btn>
            </div>
          </v-alert>

          <div v-if="nicknameDivOpen">
            <br><br>
            <v-text-field
                v-model="newName"
                label="変更したい場合は新しいニックネームを記入してください。（任意）"
                outlined
                dense
                variant="outlined"
                density="compact"
                prepend-inner-icon="mdi-account"
            />
            <v-btn
                color="primary"
                prepend-icon="mdi-content-save"
                @click="updateDisplayName"
            >
              ニックネーム変更実行
            </v-btn>
            <v-alert
                v-if="message"
                :type="alertType"
                dense
                outlined
                class="mt-2"
                v-html="message"
            />
          </div>
        </div>

        <!-- ログインフォーム -->
        <div v-if="!user && loginDiv && !showPasswordReset" class="oh3-mt-3">
          <v-text-field
              variant="outlined"
              density="compact"
              v-model="email"
              type="email"
              placeholder="メールアドレス"
              prepend-inner-icon="mdi-email-outline"
          />
          <v-text-field
              variant="outlined"
              density="compact"
              v-model="password"
              type="password"
              placeholder="パスワード"
              prepend-inner-icon="mdi-lock-outline"
          />
          <v-btn prepend-icon="mdi-login" @click="login" block>
            ログイン
          </v-btn>

          <!-- パスワードを忘れた方 -->
          <div class="text-center mt-2">
            <a href="#" class="forgot-password-link" @click.prevent="showPasswordReset = true">
              パスワードを忘れた方
            </a>
          </div>

          <!-- Googleログイン（全デバイスで表示） -->
          <v-divider class="my-4">
            <span class="oh3-text-sm oh3-text-secondary">または</span>
          </v-divider>

          <v-btn
              prepend-icon="mdi-google"
              @click="signInWithGoogle"
              variant="outlined"
              block
          >
            Googleでログイン
          </v-btn>

          <v-alert
              type="warning"
              variant="tonal"
              density="compact"
              class="mt-2"
              v-if="errorMsg"
          >
            {{ errorMsg }}
          </v-alert>
        </div>

        <!-- 新規登録フォーム -->
        <div v-if="!user && signUpDiv" class="oh3-mt-3">
            <!-- 登録のメリット -->
            <v-alert
                type="info"
                variant="tonal"
                density="comfortable"
                class="mb-4"
                border="start"
            >
              <div class="oh3-font-bold oh3-mb-2">アカウント登録でできること</div>
              <ul class="oh3-ml-4" style="line-height: 1.8;">
                <li>お気に入りレイヤーの保存・同期</li>
                <li>最近使用したレイヤーの同期</li>
                <li>複数デバイスで設定を共有</li>
                <li>コメントの投稿</li>
                <li>マイルーム機能の利用</li>
                <li>作成した地図の保存</li>
                <li v-if="!isMobileDevice">各種ファイル・画像のアップロード（GeoTIFF、SIMA、GeoJSON、KML/KMZ、GPX等）</li>
              </ul>
            </v-alert>

            <v-text-field
                variant="outlined"
                density="compact"
                v-model="nickname"
                type="text"
                placeholder="ニックネーム"
                prepend-inner-icon="mdi-account"
            />
            <v-text-field
                variant="outlined"
                density="compact"
                v-model="email"
                type="email"
                placeholder="メールアドレス"
                prepend-inner-icon="mdi-email-outline"
            />
            <v-text-field
                variant="outlined"
                density="compact"
                v-model="password"
                type="password"
                placeholder="パスワード"
                prepend-inner-icon="mdi-lock-outline"
            />
            <v-btn prepend-icon="mdi-account-plus" @click="signUp" block>
              新規登録
            </v-btn>

            <!-- Googleで登録（全デバイスで表示） -->
            <v-divider class="my-4">
              <span class="oh3-text-sm oh3-text-secondary">または</span>
            </v-divider>

            <v-btn
                prepend-icon="mdi-google"
                @click="signInWithGoogle"
                variant="outlined"
                block
            >
              Googleで登録
            </v-btn>

            <v-alert
                type="warning"
                variant="tonal"
                density="compact"
                class="mt-2 py-1 px-2"
                v-if="errorMsg"
            >
              {{ errorMsg }}
            </v-alert>
        </div>

        <!-- 初回Googleログイン時のニックネーム入力ダイアログ -->
        <v-dialog v-model="showNicknameDialog" max-width="500" persistent>
          <v-card>
            <v-card-title class="text-h6 d-flex align-center">
              <v-icon color="primary" class="mr-2">mdi-account-edit</v-icon>
              ニックネームを設定してください
            </v-card-title>
            <v-card-text>
              <p class="mb-4">OH3で表示されるニックネームを設定してください。後から変更も可能です。</p>
              <v-text-field
                  v-model="tempNickname"
                  variant="outlined"
                  density="compact"
                  placeholder="ニックネーム"
                  prepend-inner-icon="mdi-account"
                  :rules="[v => !!v || 'ニックネームを入力してください']"
              />
              <v-alert
                  v-if="nicknameError"
                  type="error"
                  variant="tonal"
                  density="compact"
                  class="mt-2"
              >
                {{ nicknameError }}
              </v-alert>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn
                  color="primary"
                  @click="saveNicknameForGoogleUser"
                  :disabled="!tempNickname || tempNickname.trim() === ''"
              >
                設定する
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <!-- パスワードリセットフォーム -->
        <div v-if="showPasswordReset" class="oh3-mt-3">
          <v-alert
            type="info"
            variant="tonal"
            density="comfortable"
            class="mb-4"
          >
            登録したメールアドレスを入力してください。パスワード再設定用のメールを送信します。
          </v-alert>

          <v-text-field
            variant="outlined"
            density="compact"
            v-model="resetEmail"
            type="email"
            placeholder="メールアドレス"
            prepend-inner-icon="mdi-email-outline"
          />

          <v-btn
            prepend-icon="mdi-email-send"
            @click="sendPasswordReset"
            block
            :loading="resetLoading"
            :disabled="!resetEmail"
          >
            パスワードリセットメールを送信
          </v-btn>

          <div class="text-center mt-3">
            <a href="#" class="forgot-password-link" @click.prevent="backToLogin">
              ← {{ user ? '戻る' : 'ログインに戻る' }}
            </a>
          </div>

          <v-alert
            v-if="resetMessage"
            :type="resetAlertType"
            variant="tonal"
            density="compact"
            class="mt-3"
          >
            {{ resetMessage }}
          </v-alert>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" prepend-icon="mdi-close" @click="s_dialogForLogin = false">
          閉じる
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- iOS Google ログイン用メッセージダイアログ -->
  <MessageDialog
    v-model="iosLoginInfoDialogOpen"
    :title="iosLoginInfoTitle"
    :content-html="iosLoginInfoMessage"
    :max-width="600"
    :icon="iosLoginInfoIcon"
    :icon-color="iosLoginInfoIconColor"
    :show-close-icon="true"
    :show-title-divider="true"
  />

</template>

<script>
import store from '@/store'
import { db, auth } from '@/firebase'
import firebase from '@/firebase'
import { user } from '@/authState'
import { showSuccess, showWarning } from '@/js/utils/messageHelper'
import { escapeHtml } from '@/js/utils/sanitize'
import MessageDialog from '@/components/Message-Dialog.vue'

export default {
  name: 'LoginManager',
  components: { MessageDialog },
  data () {
    return {

      nicknameDivOpen: false,
      // ローカル状態
      isLoggedIn: false,
      loginDiv: false,
      signUpDiv: false,

      newName: '',
      message: '',
      alertType: 'info',

      nickname: '',
      email: '',
      password: '',
      errorMsg: '',

      // Google ログイン用
      showNicknameDialog: false,
      tempNickname: '',
      nicknameError: '',

      // authState の参照（テンプレートから使えるように data に載せる）
      user,

      authUnsubscribe: null,

      // iOS Google ログイン用メッセージダイアログ
      iosLoginInfoDialogOpen: false,
      iosLoginInfoTitle: '',
      iosLoginInfoMessage: '',
      iosLoginInfoIcon: 'mdi-information',
      iosLoginInfoIconColor: 'primary',

      // アカウント削除用
      showDeleteConfirm: false,
      deleteLoading: false,

      // パスワードリセット用
      showPasswordReset: false,
      resetEmail: '',
      resetLoading: false,
      resetMessage: '',
      resetAlertType: 'info',
    }
  },
  computed: {
    // ダイアログ開閉は既存の Vuex を利用
    s_dialogForLogin: {
      get () {
        return store.state.dialogForLogin
      },
      set (v) {
        store.state.dialogForLogin = v
      }
    },
    s_myNickname () {
      return store.state.myNickname
    },
    displayNameToShow () {
      const n1 = this.newName
      const n2 = this.s_myNickname
      const n3 = this.user && this.user.displayName
      return n1 || n2 || n3 || ''
    },
    isMobileDevice () {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    }
  },
  watch: {
    s_dialogForLogin (newVal) {
      if (newVal && !this.user) {
        // ダイアログが開かれた時、ログインしていない場合
        const mode = store.state.initialLoginMode
        if (mode === 'signup') {
          this.signUpDiv = true
          this.loginDiv = false
        } else {
          this.loginDiv = true
          this.signUpDiv = false
        }
        // モードを一度使ったらリセット
        store.state.initialLoginMode = 'login'
      }
    }
  },
  methods: {
    async createUserDirectory () {
      try {
        if (!this.user || !this.user.getIdToken) return
        const token = await this.user.getIdToken()
        const response = await fetch('/oh3/php/create_directory.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ idToken: token }),
        })
        const data = await response.json()
        if (!response.ok) {
          console.error('エラー:', data)
          return
        }
      } catch (e) {
        console.error('ディレクトリ作成エラー:', e)
      }
    },

    updateDisplayName () {
      const u = auth.currentUser
      if (!u) {
        this.message = 'ログインユーザーが見つかりません'
        this.alertType = 'error'
        return
      }
      if (!this.newName) {
        this.message = '新ニックネームを入力してください'
        this.alertType = 'error'
        return
      }
      u.updateProfile({ displayName: this.newName })
          .then(() => {
            this.message = 'ニックネームを変更しました。<br>念の為OH3を一度閉じて<br>再読み込みしてください。'
            this.alertType = 'success'
            store.state.myNickname = this.newName
            const el = document.querySelector('#drag-handle-menuDialog-map01')
            if (el) {
              el.innerHTML = `<span class="oh3-text-lg">メニュー ようこそ${escapeHtml(this.displayNameToShow)}さん</span>`
            }
          })
          .catch(err => {
            console.error(err)
            this.message = '更新に失敗しました'
            this.alertType = 'error'
          })
    },

    openPasswordResetForLoggedIn () {
      // ログイン中のユーザーのメールアドレスを自動入力
      if (this.user && this.user.email) {
        this.resetEmail = this.user.email
      }
      this.showPasswordReset = true
    },

    async deleteAccount () {
      this.deleteLoading = true
      try {
        const u = firebase.auth().currentUser
        if (!u) {
          showWarning('ユーザー情報が見つかりません。')
          return
        }

        // Firestoreのusersドキュメントに削除フラグを記録（データは残す）
        const uid = u.uid
        try {
          await db.collection('users').doc(uid).set({
            deletedAt: new Date().toISOString(),
            // 個人情報保護: email/displayNameは保存しない
          }, { merge: true })
        } catch (e) {
          console.warn('Firestore削除フラグの書き込みに失敗（続行）:', e)
        }

        // Firebase Authアカウントを削除
        await u.delete()

        // ローカル状態をクリア
        store.state.userId = 'dummy'
        store.state.myNickname = ''
        store.state.fetchImagesFire = !store.state.fetchImagesFire
        localStorage.setItem('lastUserId', '')
        localStorage.setItem('lastNickname', '')
        this.isLoggedIn = false
        this.showDeleteConfirm = false

        showSuccess('アカウントを削除しました。ご利用ありがとうございました。')

        // 画面をリロードして状態を完全にクリア
        setTimeout(() => {
          window.location.reload()
        }, 2000)

      } catch (e) {
        console.error('アカウント削除エラー:', e.code, e.message)
        if (e.code === 'auth/requires-recent-login') {
          showWarning('セキュリティのため、再ログインしてからアカウントを削除してください。一度ログアウトして、再度ログインしてからお試しください。')
        } else {
          showWarning(`アカウント削除に失敗しました（${e.code || e.message}）`)
        }
      } finally {
        this.deleteLoading = false
      }
    },

    logOut () {
      const run = async () => {
        try {
          await firebase.auth().signOut()
          store.state.userId = 'dummy'
          store.state.fetchImagesFire = !store.state.fetchImagesFire
          const el = document.querySelector('#drag-handle-menuDialog-map01')
          if (el) el.innerHTML = '<span class="oh3-text-lg">メニュー</span>'
          this.isLoggedIn = false
          localStorage.setItem('lastUserId', '')
          localStorage.setItem('lastNickname', '')
          // showSuccess('ログアウトしました') // 強制リロードのためコメントアウト（将来復活させる可能性あり）

          // ログアウト後、画面を強制リロードして状態を完全にクリア
          setTimeout(() => {
            window.location.reload()
          }, 500) // メッセージを表示してからリロード
        } catch (e) {
          console.error('ログアウトエラー:', e.message)
        }
      }
      run()
    },

    signUp () {
      // フィールドごとの入力チェック
      const missing = []
      if (!this.nickname || this.nickname.trim() === '') missing.push('ニックネーム')
      if (!this.email || this.email.trim() === '') missing.push('メールアドレス')
      if (!this.password) missing.push('パスワード')
      if (missing.length > 0) {
        showWarning(`${missing.join('、')}が入力されていません。`)
        return
      }
      const run = async () => {
        try {
          await firebase.auth().createUserWithEmailAndPassword(this.email, this.password)
          await firebase.auth().currentUser.updateProfile({ displayName: this.nickname })
          await this.createUserDirectory()
          showSuccess(`登録成功！ようこそ、${this.nickname} さん！`)
          store.state.myNickname = this.nickname
          this.errorMsg = ''
          this.signUpDiv = false
        } catch (e) {
          console.error('サインアップ失敗:', e.message, e.code)
          switch (e.code) {
            case 'auth/email-already-in-use':
              this.errorMsg = 'このメールアドレスは既に登録されています。ログインするか、別のメールアドレスを使用してください。'
              break
            case 'auth/invalid-email':
              this.errorMsg = 'メールアドレスの形式が正しくありません。例: user@example.com'
              break
            case 'auth/weak-password':
              this.errorMsg = 'パスワードが短すぎます。6文字以上で入力してください。'
              break
            case 'auth/operation-not-allowed':
              this.errorMsg = 'メール/パスワードでの登録が無効になっています。管理者にお問い合わせください。'
              break
            case 'auth/too-many-requests':
              this.errorMsg = 'リクエストが多すぎます。しばらく待ってから再度お試しください。'
              break
            case 'auth/network-request-failed':
              this.errorMsg = 'ネットワークエラーです。インターネット接続を確認してください。'
              break
            default:
              this.errorMsg = `登録に失敗しました（${e.code || '不明なエラー'}）`
          }
        }
      }
      run()
    },

    login () {
      // フィールドごとの入力チェック
      const missing = []
      if (!this.email || this.email.trim() === '') missing.push('メールアドレス')
      if (!this.password) missing.push('パスワード')
      if (missing.length > 0) {
        showWarning(`${missing.join('、')}が入力されていません。`)
        return
      }
      const run = async () => {
        try {
          const cred = await firebase.auth().signInWithEmailAndPassword(this.email, this.password)
          const u = cred.user
          if (!u) {
            this.errorMsg = 'ログインは成功しましたが、ユーザー情報が取得できません'
            return
          }

          await this.createUserDirectory()
          this.errorMsg = 'ログイン成功'
          this.loginDiv = false
          store.state.userId = u.uid
          store.state.fetchImagesFire = !store.state.fetchImagesFire

          // lastUserIdを保存（お気に入り読み込みのキーとして使用）
          localStorage.setItem('lastUserId', u.uid)
          localStorage.setItem('lastNickname', u.displayName || '')

          // ログイン時にお気に入りレイヤーと最近使用レイヤーを再読み込み
          await store.dispatch('loadFavoriteLayersFromStorage')
          await store.dispatch('loadRecentLayersFromStorage')

          // ログイン時にユーザー設定（レイヤー、ズーム、位置）を復帰
          const app = this.$root
          if (app && typeof app.restoreUserSettings === 'function') {
            // DBになければ無視（alertOnMissing: false）
            await app.restoreUserSettings({ alertOnMissing: false })
          }

          // 所属グループ名の取得は既存ロジックに委ねる（必要ならここで store へ反映）
          const userDoc = await db.collection('users').doc(u.uid).get()
          const groups = userDoc.exists ? userDoc.data().groups : []
          if (groups && groups.length > 0) {
            const groupId = groups[0]
            const groupDoc = await db.collection('groups').doc(groupId).get()
            if (groupDoc.exists) {
              // 必要に応じて store.commit などで扱う
            }
          }
        } catch (e) {
          console.error('ログイン失敗:', e.message, e.code)
          switch (e.code) {
            case 'auth/user-not-found':
              this.errorMsg = 'このメールアドレスは登録されていません。新規登録してください。'
              break
            case 'auth/wrong-password':
              this.errorMsg = 'パスワードが正しくありません。再入力するか、「パスワードを忘れた方」から再設定してください。'
              break
            case 'auth/invalid-email':
              this.errorMsg = 'メールアドレスの形式が正しくありません。例: user@example.com'
              break
            case 'auth/user-disabled':
              this.errorMsg = 'このアカウントは無効になっています。管理者にお問い合わせください。'
              break
            case 'auth/too-many-requests':
              this.errorMsg = 'ログイン試行回数が多すぎます。しばらく待ってから再度お試しください。'
              break
            case 'auth/network-request-failed':
              this.errorMsg = 'ネットワークエラーです。インターネット接続を確認してください。'
              break
            case 'auth/invalid-credential':
              this.errorMsg = 'メールアドレスまたはパスワードが正しくありません。'
              break
            default:
              this.errorMsg = `ログインに失敗しました（${e.code || '不明なエラー'}）`
          }
        }
      }
      run()
    },

    // Googleログイン処理
    async signInWithGoogle () {
      try {

        this.errorMsg = ''
        const provider = new firebase.auth.GoogleAuthProvider()

        // iOS Safari対策: account選択を強制
        provider.setCustomParameters({
          prompt: 'select_account'
        })

        // モバイル判定
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
        const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent)

        // ★ iOS Safari の ITP 問題でリダイレクトが機能しないため、
        // ★ すべてのデバイスでポップアップ方式を使用

        const result = await firebase.auth().signInWithPopup(provider)
        const user = result.user

        if (!user) {
          this.errorMsg = 'Googleログインに失敗しました'
          return
        }


        // ユーザーディレクトリを作成
        await this.createUserDirectory()

        // displayNameが設定されていない場合、ニックネーム入力を促す
        if (!user.displayName || user.displayName.trim() === '') {
          this.showNicknameDialog = true
          // ダイアログを閉じないようにする
          return
        }

        // displayNameがある場合はそのまま登録完了
        await this.completeGoogleLogin(user)

      } catch (e) {
        console.error('❌ Googleログインエラー:', e)
        console.error('   code:', e.code)
        console.error('   message:', e.message)

        const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent)

        if (e.code === 'auth/popup-closed-by-user') {
          this.errorMsg = 'ログインがキャンセルされました'
        } else if (e.code === 'auth/popup-blocked') {
          if (isIOS) {
            this.errorMsg = 'ポップアップがブロックされました。\n\niOS Safari の場合:\n設定 > Safari > ポップアップブロック を オフ にしてください。'
            // iOS用スタイリッシュなダイアログ表示
            this.showIosLoginInfo({
              title: 'ポップアップがブロックされました',
              message: `
                <div class="ios-login-message">
                  <p class="ios-login-intro">
                    <strong>Google ログインにはポップアップが必要です。</strong>
                  </p>
                  <div class="ios-login-info-box">
                    <p class="ios-login-info-title">
                      📱 Safari のポップアップブロックを解除する方法
                    </p>
                    <ol class="ios-login-steps">
                      <li class="ios-login-step">iPhone設定アプリを開く</li>
                      <li class="ios-login-step">「Safari」を選択</li>
                      <li class="ios-login-step">「ポップアップブロック」をオフにする</li>
                      <li>Safariに戻ってログインをやり直す</li>
                    </ol>
                  </div>
                  <p class="ios-login-tip">
                    💡 ログイン完了後は、セキュリティのためポップアップブロックを再度オンに戻すことをおすすめします。
                  </p>
                </div>
              `,
              icon: 'mdi-block-helper',
              iconColor: 'warning'
            })
          } else {
            this.errorMsg = 'ポップアップがブロックされました。ブラウザの設定でポップアップを許可してください。'
          }
        } else if (e.code === 'auth/account-exists-with-different-credential') {
          this.errorMsg = 'このメールアドレスは既に別の方法（メール・パスワード）で登録されています。メールアドレスとパスワードでログインしてください。'
        } else {
          this.errorMsg = `Googleログインに失敗しました: ${e.message}`
          // iOS用エラー詳細ダイアログ
          if (isIOS) {
            this.showIosLoginInfo({
              title: 'Google ログインエラー',
              message: `
                <div class="ios-login-message">
                  <p class="ios-login-intro">
                    <strong>ログインに失敗しました。</strong>
                  </p>
                  <div class="ios-login-error-box">
                    <p class="ios-login-error-title">
                      エラー詳細
                    </p>
                    <p class="ios-login-error-detail">
                      <strong>コード:</strong> ${e.code || 'なし'}
                    </p>
                    <p class="ios-login-error-detail oh3-m-0">
                      <strong>メッセージ:</strong> ${e.message || 'なし'}
                    </p>
                  </div>
                  <p class="ios-login-tip">
                    💡 問題が解決しない場合は、メールアドレスとパスワードでのログインをお試しください。
                  </p>
                </div>
              `,
              icon: 'mdi-alert-circle',
              iconColor: 'error'
            })
          }
        }
      }
    },

    // Googleログイン後のニックネーム保存
    async saveNicknameForGoogleUser () {
      if (!this.tempNickname || this.tempNickname.trim() === '') {
        this.nicknameError = 'ニックネームを入力してください'
        return
      }

      try {
        const user = firebase.auth().currentUser
        if (!user) {
          this.nicknameError = 'ユーザー情報が見つかりません'
          return
        }


        // displayNameを更新
        await user.updateProfile({ displayName: this.tempNickname })

        // ダイアログを閉じる
        this.showNicknameDialog = false
        this.nicknameError = ''

        // ログイン完了処理
        await this.completeGoogleLogin(user)

        // 一時変数をクリア
        this.tempNickname = ''

      } catch (e) {
        console.error('ニックネーム設定エラー:', e)
        this.nicknameError = `ニックネームの設定に失敗しました: ${e.message}`
      }
    },

    // リダイレクト結果をチェック
    // ★ ポップアップ方式に統一したため、この処理は基本的に何もしない
    async checkRedirectResult () {
      try {
        // 念のため古いリダイレクトフラグをクリア
        const redirectStarted = sessionStorage.getItem('oh3_google_redirect_started')
        if (redirectStarted) {
          sessionStorage.removeItem('oh3_google_redirect_started')
        }

        // getRedirectResultは呼ぶが、結果は期待しない（ポップアップ方式なので）
        const result = await firebase.auth().getRedirectResult()

        if (result.user) {
          // 万が一リダイレクトログインが成功した場合も処理する
          await this.createUserDirectory()

          if (!result.user.displayName || result.user.displayName.trim() === '') {
            this.showNicknameDialog = true
            this.s_dialogForLogin = true
          } else {
            await this.completeGoogleLogin(result.user)
          }
        }
      } catch (error) {
        console.error('❌ [checkRedirectResult] エラー:', error)
        sessionStorage.removeItem('oh3_google_redirect_started')
      }
    },

    // Googleログイン完了処理
    async completeGoogleLogin (user) {
      try {
        store.state.userId = user.uid
        store.state.myNickname = user.displayName
        store.state.fetchImagesFire = !store.state.fetchImagesFire

        // localStorageに保存
        localStorage.setItem('lastUserId', user.uid)
        localStorage.setItem('lastNickname', user.displayName || '')

        // お気に入りレイヤーと最近使用レイヤーを再読み込み
        await store.dispatch('loadFavoriteLayersFromStorage')
        await store.dispatch('loadRecentLayersFromStorage')

        // ユーザー設定を復帰
        const app = this.$root
        if (app && typeof app.restoreUserSettings === 'function') {
          await app.restoreUserSettings({ alertOnMissing: false })
        }

        // メニューヘッダーを更新
        const el = document.querySelector('#drag-handle-menuDialog-map01')
        if (el) {
          el.innerHTML = `<span class="oh3-text-lg">メニュー ようこそ${escapeHtml(user.displayName || '')}さん</span>`
        }

        // ダイアログを閉じる
        this.loginDiv = false
        this.signUpDiv = false

        showSuccess(`ようこそ、${user.displayName}さん！Googleアカウントでログインしました`)

      } catch (e) {
        console.error('ログイン完了処理エラー:', e)
        this.errorMsg = `ログイン完了処理に失敗しました: ${e.message}`
      }
    },

    // iOS用スタイリッシュなログイン情報ダイアログを表示
    showIosLoginInfo ({ title, message, icon = 'mdi-information', iconColor = 'primary' }) {
      this.iosLoginInfoTitle = title
      this.iosLoginInfoMessage = message
      this.iosLoginInfoIcon = icon
      this.iosLoginInfoIconColor = iconColor
      this.iosLoginInfoDialogOpen = true
    },

    // パスワードリセットメール送信
    async sendPasswordReset () {
      if (!this.resetEmail) {
        this.resetMessage = 'メールアドレスを入力してください'
        this.resetAlertType = 'warning'
        return
      }

      this.resetLoading = true
      this.resetMessage = ''

      try {
        // メールを日本語で送信
        firebase.auth().languageCode = 'ja'
        await firebase.auth().sendPasswordResetEmail(this.resetEmail)
        this.resetMessage = 'パスワードリセットメールを送信しました。メールをご確認ください。'
        this.resetAlertType = 'success'
        // 成功したらメールアドレスをクリア
        this.resetEmail = ''
      } catch (e) {
        console.error('パスワードリセットエラー:', e)
        switch (e.code) {
          case 'auth/user-not-found':
            this.resetMessage = 'このメールアドレスは登録されていません'
            break
          case 'auth/invalid-email':
            this.resetMessage = '無効なメールアドレスです'
            break
          case 'auth/too-many-requests':
            this.resetMessage = 'リクエストが多すぎます。しばらく待ってから再度お試しください'
            break
          default:
            this.resetMessage = `メール送信に失敗しました: ${e.message}`
        }
        this.resetAlertType = 'error'
      } finally {
        this.resetLoading = false
      }
    },

    // ログイン画面に戻る
    backToLogin () {
      this.showPasswordReset = false
      this.loginDiv = true
      this.resetEmail = ''
      this.resetMessage = ''
    },
  },
  async mounted () {
    // メニューヘッダ初期化（互換維持）
    const el = document.querySelector('#drag-handle-menuDialog-map01')
    if (el) el.innerHTML = '<span class="oh3-text-lg">メニュー</span>'

    // リダイレクト結果をチェック（iOS対応）
    await this.checkRedirectResult()

    // 認証状態の監視
    this.authUnsubscribe = firebase.auth().onAuthStateChanged(async (u) => {
      if (u && u.email) {
        store.state.myNickname = u.displayName || ''
        this.newName = u.displayName || ''
        this.isLoggedIn = true
        this.$nextTick(() => {
          if (el) {
            el.innerHTML = `<span class="oh3-text-lg">メニュー ようこそ${escapeHtml(u.displayName || '')}さん</span>`
          }
        })
      } else {
        console.warn('⚠️ ログイン中のユーザーが見つかりません')
      }
    })
  },
  beforeUnmount () {
    if (typeof this.authUnsubscribe === 'function') {
      this.authUnsubscribe()
      this.authUnsubscribe = null
    }
  }
}
</script>

<style scoped>
/* iOS ログインメッセージスタイル */
.ios-login-message {
  line-height: 1.8;
}

.ios-login-intro {
  margin-bottom: var(--oh3-spacing-4);
  font-size: var(--oh3-font-size-md);
}

.ios-login-info-box {
  background: #e3f2fd;
  padding: var(--oh3-spacing-4);
  border-radius: var(--oh3-radius-lg);
  border-left: 4px solid #2196f3;
  margin-bottom: var(--oh3-spacing-4);
}

.ios-login-info-title {
  margin: 0 0 var(--oh3-spacing-3) 0;
  font-weight: var(--oh3-font-weight-semibold);
  color: #1565c0;
}

.ios-login-steps {
  margin: 0;
  padding-left: var(--oh3-spacing-5);
  color: #1565c0;
}

.ios-login-step {
  margin-bottom: var(--oh3-spacing-2);
}

.ios-login-error-box {
  background: #ffebee;
  padding: var(--oh3-spacing-4);
  border-radius: var(--oh3-radius-lg);
  border-left: 4px solid #f44336;
  margin-bottom: var(--oh3-spacing-4);
}

.ios-login-error-title {
  margin: 0 0 var(--oh3-spacing-2) 0;
  font-weight: var(--oh3-font-weight-semibold);
  color: #c62828;
}

.ios-login-error-detail {
  margin: 0 0 var(--oh3-spacing-1) 0;
  color: #c62828;
  font-size: var(--oh3-font-size-base);
}

.ios-login-tip {
  margin: 0;
  font-size: var(--oh3-font-size-base);
  color: var(--oh3-color-text-secondary);
}

/* パスワードを忘れた方リンク */
.forgot-password-link {
  color: var(--oh3-color-link);
  text-decoration: none;
  font-size: var(--oh3-font-size-base);
}

.forgot-password-link:hover {
  text-decoration: underline;
}

/* アカウント削除リンク */
.delete-account-link {
  color: var(--oh3-color-text-secondary);
  text-decoration: none;
  font-size: var(--oh3-font-size-sm);
}

.delete-account-link:hover {
  color: rgb(var(--v-theme-error));
  text-decoration: underline;
}
</style>
